/**
 * Demand → ResourceRequest adapter
 *
 * Converts backend Demand / DemandLine models into the frontend
 * ResourceRequest shape consumed by ResourceRequestsTab and the
 * planning engine.
 */

import type { Demand, DemandLine } from '@/types/demand';
import type { ResourceRequest, ResourceRequestStatus, RequestPriority } from '@/types/resourceRequest';

// ── Status mapping ────────────────────────────────────────────────────────────

const STATUS_MAP: Record<string, ResourceRequestStatus> = {
  'open':             'pending',
  'partially-filled': 'pending',
  'filled':           'accepted',
  'cancelled':        'declined',
};

function mapStatus(demandStatus: string): ResourceRequestStatus {
  return STATUS_MAP[demandStatus] ?? 'pending';
}

// ── Priority mapping ──────────────────────────────────────────────────────────

const PRIORITY_MAP: Record<string, RequestPriority> = {
  critical: 'critical',
  high:     'high',
  medium:   'medium',
  low:      'low',
};

function mapPriority(demandPriority: string): RequestPriority {
  return PRIORITY_MAP[demandPriority.toLowerCase()] ?? 'medium';
}

// ── DemandLines → monthlyHourHints ────────────────────────────────────────────

function buildMonthlyHourHints(lines: DemandLine[]): Record<string, number> {
  const hints: Record<string, number> = {};
  for (const line of lines) {
    // Use the 'month' field (e.g. "2026-06") as the key
    if (line.month) {
      hints[line.month] = line.budgetHours;
    }
  }
  return hints;
}

// ── Earliest / latest dates from DemandLines ──────────────────────────────────

function deriveStartDate(lines: DemandLine[]): string {
  if (lines.length === 0) return new Date().toISOString().split('T')[0];
  const sorted = [...lines].sort((a, b) => a.monthStart.localeCompare(b.monthStart));
  return sorted[0].monthStart;
}

function deriveEndDate(lines: DemandLine[]): string | undefined {
  if (lines.length === 0) return undefined;
  const sorted = [...lines].sort((a, b) => b.monthEnd.localeCompare(a.monthEnd));
  return sorted[0].monthEnd;
}

// ── Skills parsing ────────────────────────────────────────────────────────────

/**
 * Parse the skills JSON string into a flat string[] of skill names.
 * Handles both plain string arrays and object arrays with
 * PascalCase (C# serialised) or camelCase keys.
 */
function parseSkills(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((s: unknown) => {
      if (typeof s === 'string') return s;
      if (s && typeof s === 'object') {
        const obj = s as Record<string, unknown>;
        return String(obj.SkillName ?? obj.skillName ?? obj.Name ?? obj.name ?? '');
      }
      return '';
    }).filter(Boolean);
  } catch {
    return [];
  }
}

// ── Main adapter ──────────────────────────────────────────────────────────────

export function demandToResourceRequest(demand: Demand): ResourceRequest {
  const lines = demand.demandLines ?? [];
  const hints = buildMonthlyHourHints(lines);

  return {
    id:                   String(demand.id),
    projectId:            demand.projectId,
    projectName:          demand.projectName,
    projectCode:          demand.projectCode || undefined,
    requestedByName:      demand.requestedByName || demand.source || 'Unknown',
    requestedBySystem:    demand.source || 'EBProjects',
    role:                 demand.roleName,
    skills:               demand.skills ? parseSkills(demand.skills) : [],
    totalHoursRequested:  demand.totalHours,
    requestedStartDate:   deriveStartDate(lines),
    requestedEndDate:     deriveEndDate(lines),
    status:               mapStatus(demand.status),
    priority:             mapPriority(demand.priority),
    receivedAt:           demand.createdAt,
    notes:                demand.notes || undefined,
    monthlyHourHints:     Object.keys(hints).length > 0 ? hints : undefined,
    location:             demand.location || undefined,
  };
}

/**
 * Convert an array of Demands to ResourceRequests.
 */
export function demandsToResourceRequests(demands: Demand[]): ResourceRequest[] {
  return demands.map(demandToResourceRequest);
}

/**
 * Find the DemandLine that corresponds to a given month key ('YYYY-MM').
 * Returns undefined if no matching line exists.
 */
export function findDemandLineByMonth(
  lines: DemandLine[],
  monthKey: string,
): DemandLine | undefined {
  return lines.find(l => l.month === monthKey);
}
